import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, AlignLeft, AlignCenter, AlignRight, Minus, Undo2, Redo2, Pilcrow } from "lucide-react";

const TAGS = [["{first_name}", "First name"], ["{name}", "Full name"], ["{email}", "Email"]];

const Tool = ({ on, active, title, children, testId }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={on}
    className={`rounded p-1 transition-colors ${active ? "bg-green-100 text-green-800" : "text-slate-600 hover:bg-slate-100"}`} data-testid={testId}>{children}</button>
);

const askUrl = (label, current = "") => {
  const v = window.prompt(label, current);
  if (v === null) return null;
  const url = v.trim();
  if (url && !/^(https?:\/\/|mailto:)/i.test(url)) { window.alert("Links must start with https:// (or mailto: for email addresses)"); return null; }
  return url;
};

const Toolbar = ({ editor }) => {
  const c = () => editor.chain().focus();
  const link = () => {
    const url = askUrl("Link address (https://…)", editor.getAttributes("link").href || "");
    if (url === null) return;
    url ? c().extendMarkRange("link").setLink({ href: url }).run() : c().unsetLink().run();
  };
  const image = () => { const url = askUrl("Image address (https://…)"); if (url) c().setImage({ src: url }).run(); };
  return (
    <>
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-slate-50 px-1.5 py-1" data-testid="editor-toolbar">
      <Tool on={() => c().setParagraph().run()} active={editor.isActive("paragraph")} title="Normal text" testId="tool-paragraph"><Pilcrow size={14} /></Tool>
      <Tool on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading" testId="tool-h2"><Heading2 size={14} /></Tool>
      <Tool on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading" testId="tool-h3"><Heading3 size={14} /></Tool>
      <span className="mx-1 h-4 w-px bg-slate-200" />
      <Tool on={() => c().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testId="tool-bold"><Bold size={14} /></Tool>
      <Tool on={() => c().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testId="tool-italic"><Italic size={14} /></Tool>
      <Tool on={() => c().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testId="tool-underline"><Underline size={14} /></Tool>
      <Tool on={() => c().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testId="tool-strike"><Strikethrough size={14} /></Tool>
      <span className="mx-1 h-4 w-px bg-slate-200" />
      <Tool on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId="tool-bullets"><List size={14} /></Tool>
      <Tool on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId="tool-numbers"><ListOrdered size={14} /></Tool>
      <Tool on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testId="tool-quote"><Quote size={14} /></Tool>
      <span className="mx-1 h-4 w-px bg-slate-200" />
      {["left", "center", "right"].map((a) => {
        const Icon = { left: AlignLeft, center: AlignCenter, right: AlignRight }[a];
        return <Tool key={a} on={() => c().setTextAlign(a).run()} active={editor.isActive({ textAlign: a })} title={`Align ${a}`} testId={`tool-align-${a}`}><Icon size={14} /></Tool>;
      })}
      <span className="mx-1 h-4 w-px bg-slate-200" />
      <Tool on={link} active={editor.isActive("link")} title="Link" testId="tool-link"><Link2 size={14} /></Tool>
      <Tool on={image} title="Image from web address" testId="tool-image"><ImagePlus size={14} /></Tool>
      <Tool on={() => c().setHorizontalRule().run()} title="Divider" testId="tool-divider"><Minus size={14} /></Tool>
    </div>
    <div className="flex items-center gap-2 border-b border-border bg-slate-50/60 px-2 py-1 text-[11px] text-muted-foreground">
      <span>Personalise:</span>
      {TAGS.map(([t, l]) => <button key={t} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => c().insertContent(t).run()} className="rounded-full border border-green-200 bg-white px-2 py-0.5 font-medium text-green-800 hover:bg-green-50" data-testid={`tool-tag-${t.slice(1, -1)}`}>{l}</button>)}
      <span className="ml-auto flex">
        <Tool on={() => c().undo().run()} title="Undo" testId="tool-undo"><Undo2 size={14} /></Tool>
        <Tool on={() => c().redo().run()} title="Redo" testId="tool-redo"><Redo2 size={14} /></Tool>
      </span>
    </div>
    </>
  );
};

export const RichEmailEditor = ({ value, onChange, minHeight = "min-h-[260px]" }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false, autolink: true } }), Image, TextAlign.configure({ types: ["heading", "paragraph"] })],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: `email-editor ${minHeight} px-4 py-3 text-sm focus:outline-none`, "data-testid": "email-editor" } },
  });
  // Load new content (e.g. a template was picked) without fighting the user's typing.
  useEffect(() => { if (editor && value !== editor.getHTML()) editor.commands.setContent(value || "", { emitUpdate: false }); }, [value, editor]);
  if (!editor) return null;
  return (
    <div className="overflow-hidden rounded-md border border-border bg-white focus-within:ring-2 focus-within:ring-green-200">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};
